import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, Plus, Loader2, RefreshCw } from "lucide-react";
import { Sparkle } from "@/components/icons";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const SAMPLE = "Nose invested $20,000 into GTA 6, hoping the physical copies would sell out. He meets a friend at a coffee shop who delivers the bad news. Devastated, Nose leaves and waits at a lonely bus stop as evening falls. He arrives home to find his house burning down.";

export default function NewStoryModal({ open, onOpenChange }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [story, setStory] = useState("");
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files.slice(0, 5 - refs.length)) {
      try {
        const b64 = await new Promise((res) => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(f); });
        const { data } = await api.post(`/uploads/image`, { image_base64: b64, origin_url: window.location.origin });
        setRefs((r) => [...r, { url: data.url }]);
      } catch { toast.error("Upload failed"); }
    }
    setUploading(false);
    e.target.value = "";
  };

  const create = async () => {
    if (!story.trim()) { toast.error("Describe your story first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects`, { story, reference_images: refs.map((r) => r.url) });
      onOpenChange(false);
      setStory(""); setRefs([]);
      navigate(`/studio/${data.id}?auto=1`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-zinc-800 text-[#F0E9DD] sm:max-w-md max-h-[94vh] overflow-y-auto p-0 gap-0 [&>button]:hidden">
        <DialogDescription className="sr-only">Describe your story to generate a storyboard.</DialogDescription>
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <button onClick={() => onOpenChange(false)} data-testid="story-modal-close" className="text-gold hover:opacity-70"><X size={24} /></button>
          <DialogTitle className="font-heading font-bold text-xl text-white">New Story</DialogTitle>
          <span className="w-6" />
        </div>

        <div className="px-5 space-y-4">
          <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40">
            <div className="flex items-center justify-between mb-3">
              <span className="text-zinc-300 font-heading font-semibold">Reference Image <span className="text-zinc-600 font-normal">(Optional)</span></span>
              <div className="flex items-center gap-3">
                <button onClick={() => setRefs([])} className="text-zinc-500 hover:text-gold"><RefreshCw size={15} /></button>
                <span className="w-px h-4 bg-zinc-700" />
                <span className="text-xs font-mono border border-zinc-700 rounded-full px-2.5 py-1 text-gold">{refs.length}/5</span>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="story-ref-input" />
            <div className="flex flex-wrap gap-2">
              {refs.map((r, i) => (
                <div key={i} className="relative w-16 h-16 border border-zinc-800 rounded-md overflow-hidden">
                  <img src={r.url} alt="ref" className="w-full h-full object-cover" />
                  <button onClick={() => setRefs((x) => x.filter((_, j) => j !== i))} className="absolute top-0 right-0 bg-ink-950/80 p-0.5"><X size={12} /></button>
                </div>
              ))}
              {refs.length < 5 && (
                <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="story-add-ref" className="w-16 h-16 border border-dashed border-zinc-600 rounded-md flex items-center justify-center text-zinc-400 hover:border-gold hover:text-gold transition-colors">
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={22} />}
                </button>
              )}
            </div>
          </div>

          <div className="border border-zinc-800 rounded-xl p-4 bg-zinc-950/40 min-h-[220px] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-gold font-heading font-semibold"><Sparkle size={16} /> Your Story</div>
              <button onClick={() => setStory(SAMPLE)} data-testid="story-sample" className="text-xs text-gold hover:underline">Use sample</button>
            </div>
            <textarea value={story} onChange={(e) => setStory(e.target.value)} data-testid="story-input"
              placeholder="Describe your full story. The AI will break it into cinematic panels, then generate one image per scene…"
              className="w-full flex-1 bg-transparent text-base focus:outline-none resize-none placeholder:text-zinc-600 leading-relaxed" />
          </div>
        </div>

        <div className="px-5 pt-4 pb-5">
          <button onClick={create} disabled={busy} data-testid="create-story-submit"
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-gradient-to-b from-goldlight to-gold text-ink-950 font-heading font-black text-xl py-3.5 hover:brightness-105 transition disabled:opacity-60 shadow-[0_0_24px_rgba(198,138,91,0.35)]">
            {busy ? <Loader2 className="animate-spin" size={22} /> : <><Sparkle size={20} /> Create Storyboard</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
